<!--
 * @Description: 聊天侧边栏组件（自适应 PC/移动端）
 * 
 * PC端：固定侧边栏，默认显示在页面右侧，可配置显示在左侧
 * 移动端：抽屉模式，从右侧滑出（带遮罩）
 * 
 * 支持两种类型：
 * 1. chat - 聊天历史记录（PC显示"对话记录"，移动端显示"历史记录"）
 * 2. project - 我的项目（显示"我的项目"）
 * 3. empty - 空侧栏容器（用于仅承载自定义内容）
 * 
 * @Props:
 *   - visible: 控制显示/隐藏
 *   - type: 类型 'chat' | 'project'
 *   - preventNavigation: 阻止内部路由跳转（工具页嵌入时使用）
 *   - position: PC 端侧边栏位置 'left' | 'right'
 *   - hideHeader: PC 端是否隐藏默认标题栏
 *   - stretchToParent: PC 端宽度随父容器（工具页右侧壳层拖拽宽度），不设 216px 上限
 * 
 * @Emits:
 *   - toggle: 切换侧边栏显示/隐藏（PC端）
 *   - close: 关闭抽屉（移动端）
 *   - select: 选中某条记录/项目
 *   - new-chat: 新建聊天完成（preventNavigation 模式）
 *   - conversation-deleted: 删除了当前会话（preventNavigation 模式）
-->
<template>
    <!-- PC端：固定侧边栏（用样式收起而非 v-if，才能做过渡动画） -->
    <div
        v-if="!isMobile"
        :class="[
            'chat-sidebar max-md:!hidden',
            `chat-sidebar--${props.position}`,
            {
                'chat-sidebar--headerless': props.hideHeader,
                'chat-sidebar--pc-hidden': !visible,
                'chat-sidebar--stretch': props.stretchToParent,
            },
        ]"
        :aria-hidden="visible ? undefined : true"
    >
        <slot name="desktop-top"></slot>

        <!-- 顶部标题栏 -->
        <div v-if="!props.hideHeader" class="sidebar-header">
            <button class="toggle-btn" @click="emit('toggle')">
                <img src="@/assets/images/common/chat-slidebar-icon.png" alt="close sidebar"
                    :class="['toggle-icon', { 'toggle-icon--left': props.position === 'left' }]" />
            </button>
            <span class="sidebar-title">{{ type === 'project' ? t('workbench.myProject') : t('chat.dialogueRecord')
                }}</span>
        </div>

        <div v-else-if="type !== 'empty'" class="sidebar-section-title">
            {{ type === 'project' ? t('workbench.myProject') : t('chat.dialogueRecord') }}
        </div>

        <!-- 聊天历史记录（type === 'chat'） -->
        <template v-if="type === 'chat'">
            <!-- 内容区域 -->
            <div class="sidebar-content" @scroll="handleScroll">
                <!-- 加载状态 -->
                <div v-if="isInitialLoading" class="loading-state">
                    <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-[#99F768]"></div>
                    <p class="loading-text">{{ t('common.loading') }}</p>
                </div>

                <template v-else-if="chatHistoryList.length === 0">

                    <!-- 空状态 -->
                    <div class="empty-state">
                        <img src="@/assets/images/common/empty.png" alt="empty" class="empty-image" />
                        <p class="empty-title">{{ t('chat.noChat') }}</p>

                    </div>
                    <div class="border-t border-white/[0.05] w-full h-[1px] absolute bottom-[75px] left-0 right-0">
                    </div>
                    <!-- 空状态下的新建按钮 -->
                    <button
                        class="new-chat-btn empty-new-chat-btn !absolute bottom-3 !w-[85%] hover:scale-105 hover:!text-text-active transition-all duration-300"
                        @click="handleNewChat">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M3.33325 8H12.6666" stroke="currentColor" stroke-width="1.33333"
                                stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M8 3.33337V12.6667" stroke="currentColor" stroke-width="1.33333"
                                stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <span>{{ t('chat.newChat') }}</span>
                    </button>
                </template>


                <!-- 聊天历史记录列表 -->
                <div v-else class="history-list">
                    <div v-for="item in chatHistoryList" :key="item.id" class="history-item"
                        :class="{ 'is-active': chatDialogueStore.conversationId === String(item.id) }"
                        @click="handleSelectItem(item)">
                        <div class="item-info">
                            <span class="item-title">{{ item.title }}</span>
                        </div>
                        <div class="item-actions">
                            <!-- PC：hover 行显示按钮；hover 按钮展开菜单（移动端保持点击交互，见 drawer 部分） -->
                            <button class="more-btn" @mouseenter.stop="openItemMenu(item.id)"
                                @mouseleave.stop="scheduleCloseItemMenu(item.id)" @click.stop>
                                <svg class="more-icon" viewBox="0 0 24 24" fill="currentColor">
                                    <circle cx="5" cy="12" r="2" />
                                    <circle cx="12" cy="12" r="2" />
                                    <circle cx="19" cy="12" r="2" />
                                </svg>
                            </button>
                            <!-- 操作弹出框（重命名 + 删除） -->
                            <Transition name="popup">
                                <div v-if="activeMenuId === item.id" class="action-popup"
                                    @mouseenter="cancelCloseItemMenu" @mouseleave="scheduleCloseItemMenu(item.id)">
                                    <button class="edit-btn" @click.stop="openRenameChatDialog(item)">
                                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none"
                                            xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M9.56349 10.899C9.42711 10.899 9.29073 10.847 9.18665 10.7429C8.97847 10.5347 8.97847 10.1971 9.18665 9.98922L14.9885 4.18738C15.1967 3.97921 15.534 3.97921 15.7422 4.18738C15.9503 4.39555 15.9503 4.7332 15.7422 4.94106L9.94033 10.7429C9.8909 10.7925 9.83216 10.8318 9.76749 10.8586C9.70282 10.8854 9.63349 10.8991 9.56349 10.899Z"
                                                fill="white" />
                                            <path
                                                d="M14.6788 16.0194H5.36754C4.62107 16.0194 4.01379 15.4121 4.01379 14.6653V5.35406C4.01379 4.60727 4.62107 4 5.36722 4L10.8151 4.00031C11.1095 4.00031 11.3481 4.2389 11.3481 4.53328C11.3481 4.82767 11.1095 5.06625 10.8151 5.06625L5.36722 5.06594C5.2089 5.06594 5.07973 5.19511 5.07973 5.35374V14.665C5.07973 14.8237 5.2089 14.9528 5.36754 14.9528H14.6788C14.8375 14.9528 14.9666 14.8237 14.9666 14.665V8.71082C14.9666 8.41643 15.2055 8.17785 15.4996 8.17785C15.794 8.17785 16.0326 8.41643 16.0326 8.71082V14.6657C16.0326 15.4121 15.4253 16.0194 14.6788 16.0194Z"
                                                fill="white" />
                                        </svg>

                                        <span>{{ t('chat.rename') }}</span>
                                    </button>
                                    <button class="delete-btn" @click.stop="openDeleteChatDialog(item)">
                                        <img src="@/assets/images/common/delete.png" alt="delete" class="delete-icon" />
                                        <span>{{ t('chat.delete') }}</span>
                                    </button>
                                </div>
                            </Transition>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 底部按钮（仅在有内容时显示） -->
            <div v-if="chatHistoryList.length > 0" class="sidebar-footer border-t border-white/[0.05]">
                <button class="new-chat-btn  empty-new-chat-btn" @click="handleNewChat">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3.33325 8H12.6666" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round"
                            stroke-linejoin="round" />
                        <path d="M8 3.33337V12.6667" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round"
                            stroke-linejoin="round" />
                    </svg>
                    <span>{{ t('chat.newChat') }}</span>
                </button>
            </div>
        </template>

        <!-- 我的项目（type === 'project'） -->
        <template v-else-if="type === 'project'">
            <!-- 内容区域 -->
            <div class="sidebar-content">
                <!-- 空状态 -->
                <div v-if="projectList.length === 0" class="empty-state">
                    <img src="@/assets/images/common/empty.png" alt="empty" class="empty-image" />
                    <p class="empty-title">{{ t('workbench.noProject') }}</p>
                    <!-- 空状态下的新建按钮 -->
                    <!-- <button class="new-chat-btn empty-new-chat-btn" @click="handleAddProject">
                        <svg class="plus-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        <span>{{ t('workbench.newProject') }}</span>
                    </button> -->
                </div>
                <!-- 项目列表 -->
                <div v-else class="history-list">
                    <div v-for="item in projectList" :key="item.id" class="history-item"
                        :class="{ 'is-active': appStore.currentProject?.id === item.id }"
                        @click="handleSelectProject(item)">
                        <div class="item-info">
                            <span class="item-title">{{ item.name }}</span>
                            <span class="item-date">{{ formatProjectDate(item.createTime) }}</span>
                        </div>
                        <div class="item-actions">
                            <!-- PC：hover 按钮展开菜单（移动端保持点击交互，见 drawer 部分） -->
                            <button class="more-btn" @mouseenter.stop="openItemMenu(item.id)"
                                @mouseleave.stop="scheduleCloseItemMenu(item.id)" @click.stop>
                                <svg class="more-icon" viewBox="0 0 24 24" fill="currentColor">
                                    <circle cx="5" cy="12" r="2" />
                                    <circle cx="12" cy="12" r="2" />
                                    <circle cx="19" cy="12" r="2" />
                                </svg>
                            </button>
                            <!-- 操作弹出框（编辑 + 删除） -->
                            <Transition name="popup">
                                <div v-if="activeMenuId === item.id" class="action-popup"
                                    @mouseenter="cancelCloseItemMenu" @mouseleave="scheduleCloseItemMenu(item.id)">
                                    <button class="edit-btn" @click.stop="handleEditProject(item)">
                                        <svg class="edit-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                            stroke-width="2">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                        </svg>
                                        <span>{{ t('batchTools.editProject') }}</span>
                                    </button>
                                    <button class="delete-btn" v-if="projectList.length > 1"
                                        @click.stop="handleDeleteProject(item)">
                                        <img src="@/assets/images/common/delete.png" alt="delete" class="delete-icon" />
                                        <span>{{ t('chat.delete') }}</span>
                                    </button>
                                </div>
                            </Transition>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 底部按钮（仅在有内容时显示，PC端） -->
            <div v-if="!isMobile" class="sidebar-footer relative border-t border-white/[0.05]">
                <button class="new-chat-btn empty-new-chat-btn" @click="handleAddProject">
                    <svg class="plus-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    <span>{{ t('workbench.newProject') }}</span>
                </button>
            </div>
        </template>
    </div>

    <!-- 移动端：抽屉模式 -->
    <Teleport to="body">
        <Transition name="drawer">
            <div v-if="isMobile && visible" class="history-drawer-wrapper">
                <!-- 遮罩层 -->
                <div class="drawer-overlay" @click="handleClose"></div>

                <!-- 抽屉容器 -->
                <div class="drawer-container">
                    <!-- 顶部标题栏 -->
                    <div class="drawer-header">
                        <span class="drawer-title">{{ type === 'project' ? t('workbench.myProject') :
                            t('chat.historyRecord') }}</span>
                        <button class="close-btn" @click="handleClose">
                            <svg class="close-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>

                    <!-- 聊天历史记录（type === 'chat'） -->
                    <template v-if="type === 'chat'">
                        <div class="drawer-content" @scroll="handleScroll">
                            <!-- 加载状态 -->
                            <div v-if="isInitialLoading" class="loading-state">
                                <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-[#99F768]"></div>
                                <p class="loading-text">{{ t('common.loading') }}</p>
                            </div>

                            <!-- 空状态 -->
                            <div v-else-if="chatHistoryList.length === 0" class="empty-state">
                                <img src="@/assets/images/common/empty.png" alt="empty" class="empty-image" />
                                <p class="empty-title">{{ t('chat.noChat') }}</p>
                                <button class="new-chat-btn" @click="handleNewChat">
                                    <img src="@/assets/images/common/chat-add.png" alt="new chat"
                                        class="chat-add-icon" />
                                    <span>{{ t('chat.newChat') }}</span>
                                </button>
                            </div>

                            <!-- 聊天历史记录列表 -->
                            <div v-else class="history-list">
                                <div v-for="item in chatHistoryList" :key="item.id" class="history-item"
                                    :class="{ 'is-active': chatDialogueStore.conversationId === String(item.id) }"
                                    @click="handleSelectItemMobile(item)">
                                    <div class="item-left">
                                        <img src="@/assets/images/common/chat.png" alt="chat" class="chat-icon" />
                                        <div class="item-info">
                                            <span class="item-title">{{ item.title }}</span>
                                            <span class="item-date">{{ item.date }}</span>
                                        </div>
                                    </div>
                                    <div class="item-actions">
                                        <button class="more-btn" @click.stop="toggleItemMenu(item.id)">
                                            <svg class="more-icon" viewBox="0 0 24 24" fill="currentColor">
                                                <circle cx="5" cy="12" r="2" />
                                                <circle cx="12" cy="12" r="2" />
                                                <circle cx="19" cy="12" r="2" />
                                            </svg>
                                        </button>
                                        <!-- 操作弹出框（修改名称 + 删除） -->
                                        <Transition name="popup">
                                            <div v-if="activeMenuId === item.id" class="action-popup">
                                                <button class="edit-btn" @click.stop="openRenameChatDialog(item)">
                                                    <svg width="16" height="16" viewBox="0 0 14 14" fill="none"
                                                        xmlns="http://www.w3.org/2000/svg">
                                                        <g clip-path="url(#clip0_13701_29562)">
                                                            <path
                                                                d="M11.1533 4.35894L9.65078 2.85776C9.38418 2.59116 9.38418 2.15776 9.65078 1.89116L10.467 1.07495C10.7336 0.80835 11.167 0.80835 11.4336 1.07495L12.9348 2.57612C13.2014 2.84272 13.2014 3.27612 12.9348 3.54272L12.1186 4.35894C11.852 4.6269 11.4199 4.6269 11.1533 4.35894ZM10.2701 6.19917C10.5272 5.94214 10.5914 5.59077 10.4137 5.4144L8.58711 3.58647C8.41074 3.41011 8.05938 3.47436 7.80234 3.73003L1.11133 10.2433C0.982813 10.3718 0.902148 10.5359 0.885742 10.7013L0.888477 12.5306C0.855664 12.8751 1.12637 13.1458 1.4709 13.113L3.3002 13.1281C3.46563 13.1117 3.63105 13.031 3.7582 12.9025L10.2701 6.19917ZM12.4262 11.3712H7.65332C7.47012 11.3712 7.29512 11.4437 7.1666 11.5736C6.93281 11.8087 6.54727 12.2066 6.22598 12.5402C6.01816 12.7562 6.17129 13.1158 6.4707 13.1158H12.4221C12.8049 13.1158 13.118 12.8027 13.118 12.4199V12.0603C13.1152 11.6816 12.8063 11.3712 12.4262 11.3712Z"
                                                                fill="white" fill-opacity="0.9" />
                                                        </g>
                                                        <defs>
                                                            <clipPath id="clip0_13701_29562">
                                                                <rect width="14" height="14" fill="white" />
                                                            </clipPath>
                                                        </defs>
                                                    </svg>

                                                    <span>{{ t('chat.changeName') }}</span>
                                                </button>
                                                <button class="delete-btn" @click.stop="openDeleteChatDialog(item)">
                                                    <svg width="16" height="16" viewBox="0 0 14 14" fill="none"
                                                        xmlns="http://www.w3.org/2000/svg">
                                                        <g clip-path="url(#clip0_13701_29566)">
                                                            <path
                                                                d="M11.4377 2.64961H12.8076C13.0646 2.64961 13.2711 2.42812 13.2711 2.15332C13.2711 1.87852 13.0646 1.67207 12.8076 1.67207H9.91055V1.17441C9.91055 0.546875 9.4334 0.0273438 8.84004 0.0273438H5.16641C4.58125 0.0273438 4.0959 0.540039 4.0959 1.17441V1.6707H1.19199C0.934961 1.6707 0.728516 1.89219 0.728516 2.16699C0.728516 2.4418 0.934961 2.66328 1.19199 2.66328H2.56191L11.4377 2.64961ZM11.0002 3.46855L2.99941 3.48223C2.71914 3.48223 2.49082 3.72285 2.49082 4.01816V12.168C2.49082 13.1605 3.26055 13.9727 4.20117 13.9727H9.79844C10.7391 13.9727 11.5088 13.1605 11.5088 12.168V4.00586C11.5088 3.70918 11.2805 3.46855 11.0002 3.46855Z"
                                                                fill="#FF4548" />
                                                        </g>
                                                        <defs>
                                                            <clipPath id="clip0_13701_29566">
                                                                <rect width="14" height="14" fill="white" />
                                                            </clipPath>
                                                        </defs>
                                                    </svg>

                                                    <span>{{ t('chat.delete') }}</span>
                                                </button>
                                            </div>
                                        </Transition>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 底部按钮 -->
                        <div v-if="chatHistoryList.length > 0" class="drawer-footer">
                            <button class="new-chat-btn" @click="handleNewChat">
                                <img src="@/assets/images/common/chat-add.png" alt="new chat" class="chat-add-icon" />
                                <span>{{ t('chat.newChat') }}</span>
                            </button>
                        </div>
                    </template>

                    <!-- 我的项目（type === 'project'） -->
                    <template v-else-if="type === 'project'">
                        <div class="drawer-content">
                            <!-- 空状态 -->
                            <div v-if="projectList.length === 0" class="empty-state">
                                <img src="@/assets/images/common/empty.png" alt="empty" class="empty-image" />
                                <p class="empty-title">{{ t('noProject') }}</p>
                                <button class="new-chat-btn" @click="handleAddProject">
                                    <svg class="plus-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    <span>{{ t('workbench.newProject') }}</span>
                                </button>
                            </div>

                            <!-- 项目列表 -->
                            <div v-else class="history-list">
                                <div v-for="item in projectList" :key="item.id" class="history-item"
                                    :class="{ 'is-active': appStore.currentProject?.id === item.id }"
                                    @click="handleSelectProjectMobile(item)">
                                    <div class="item-info">
                                        <span class="item-title">{{ item.name }}</span>
                                        <span class="item-date">{{ formatProjectDate(item.createTime) }}</span>
                                    </div>
                                    <div class="item-actions">
                                        <button class="more-btn" @click.stop="toggleItemMenu(item.id)">
                                            <svg class="more-icon" viewBox="0 0 24 24" fill="currentColor">
                                                <circle cx="5" cy="12" r="2" />
                                                <circle cx="12" cy="12" r="2" />
                                                <circle cx="19" cy="12" r="2" />
                                            </svg>
                                        </button>
                                        <Transition name="popup">
                                            <div v-if="activeMenuId === item.id" class="action-popup">
                                                <button class="edit-btn" @click.stop="handleEditProject(item)">
                                                    <svg class="edit-icon" viewBox="0 0 24 24" fill="none"
                                                        stroke="currentColor" stroke-width="2">
                                                        <path
                                                            d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                        <path
                                                            d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                    </svg>
                                                    <span>{{ t('batchTools.editProject') }}</span>
                                                </button>
                                                <button class="delete-btn" @click.stop="handleDeleteProject(item)"
                                                    v-if="projectList.length > 1">
                                                    <img src="@/assets/images/common/delete.png" alt="delete"
                                                        class="delete-icon" />
                                                    <span>{{ t('chat.delete') }}</span>
                                                </button>
                                            </div>
                                        </Transition>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 底部按钮 -->
                        <div class="drawer-footer">
                            <button class="new-chat-btn" @click="handleAddProject">
                                <svg class="plus-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2">
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                                <span>{{ t('workbench.newProject') }}</span>
                            </button>
                        </div>
                    </template>
                </div>
            </div>
        </Transition>
    </Teleport>

    <!-- 新增/编辑项目弹窗 -->
    <Teleport to="body">
        <transition v-if="projectDialogVisible" name="fade">
            <div class="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999]"
                @click.self="projectDialogVisible = false">
                <div class="rename-chat-modal">
                    <div class="rename-chat-header">
                        <h2 class="text-white text-lg font-medium">
                            {{ editingProject ? t('batchTools.editProject') : t('batchTools.addProject') }}
                        </h2>
                        <span
                            class="text-2xl cursor-pointer hover:text-text-active transition-all duration-300 hover:scale-110"
                            @click="projectDialogVisible = false">
                            ×
                        </span>
                    </div>
                    <div class="rename-chat-body">
                        <el-input class="h-[44px]" v-model="projectNameInput"
                            :placeholder="t('batchTools.placeholderName')" @keyup.enter="handleSubmitProject" />
                    </div>
                    <div class="rename-chat-footer">
                        <el-button @click="projectDialogVisible = false"
                            class="hover:!border-white/30 hover:!bg-transparent hover:!text-white">
                            {{ t('batchTools.cancel') }}
                        </el-button>
                        <el-button class="rename-chat-btn rename-chat-btn-confirm" :loading="projectLoading"
                            :disabled="!projectNameInput.trim()" type="primary" @click="handleSubmitProject">
                            {{ t('batchTools.confirm') }}
                        </el-button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- 重命名聊天弹窗（PC） -->
        <transition v-if="renameChatDialogVisible" name="fade">
            <div class="rename-chat-overlay fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black/50 backdrop-blur-sm z-[9999]"
                :class="isMobile ? 'mobile-customize-style' : ''">
                <div class="rename-chat-modal" role="dialog" aria-modal="true">
                    <div class="rename-chat-header">
                        <h2 class="rename-chat-title">{{ t('chat.renameChatTitle') }}</h2>
                        <button class="rename-chat-close" type="button" :disabled="renameChatLoading"
                            @click="handleCancelRenameChat">
                            ×
                        </button>
                    </div>
                    <div class="rename-chat-body">
                        <div class="rename-chat-input-wrap">
                            <el-input class="rename-chat-input" :class="{ 'is-exceed': renameChatIsExceed }"
                                v-model="renameChatInput" :placeholder="t('chat.renameChatPlaceholder')" type="textarea"
                                :autosize="{ minRows: 1, maxRows: 3 }" resize="none"
                                @keydown.enter.exact.prevent="handleConfirmRenameChat" />
                            <div class="rename-chat-count" :class="{ exceed: renameChatIsExceed }">
                                {{ (renameChatInput || '').length }} / {{ RENAME_CHAT_MAXLEN }}
                            </div>
                            <div v-if="renameChatIsExceed" class="rename-chat-limit-tip">
                                {{ t('chat.renameChatLimitTip', { max: RENAME_CHAT_MAXLEN }) }}
                            </div>
                        </div>
                    </div>
                    <div class="rename-chat-footer">
                        <el-button class="rename-chat-btn rename-chat-btn-cancel" :disabled="renameChatLoading"
                            @click="handleCancelRenameChat">
                            {{ t('common.cancel') }}
                        </el-button>
                        <el-button class="rename-chat-btn rename-chat-btn-confirm" :loading="renameChatLoading"
                            :disabled="!canConfirmRenameChat" type="primary" @click="handleConfirmRenameChat">
                            {{ t('common.confirm') }}
                        </el-button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- 删除确认弹窗 -->
        <!-- <el-dialog v-model="deleteDialogVisible" :title="t('batchTools.deleteConfirmTitle')" width="500px"
            class="max-w-[92vw]">
            <p>{{ t('batchTools.deleteConfirmMessage') }}</p>
            <template #footer>
                <el-button class="hover:!border-white/30 hover:!bg-transparent hover:!text-white"
                    @click="deleteDialogVisible = false">
                    {{ t('batchTools.cancel') }}
                </el-button>
                <el-button class="hover:!bg-[var(--active-btn)] !text-black hover:!border-transparent"
                    :loading="deleteLoading" type="primary" @click="handleConfirmDelete">
                    {{ t('batchTools.confirm') }}
                </el-button>
            </template>
        </el-dialog> -->

        <Transition name="modal-fade">
            <div v-if="deleteDialogVisible" class="delete-chat-modal-overlay">
                <div class="delete-chat-modal">
                    <!-- 标题行 -->
                    <div class="modal-header">
                        <h2 class="modal-title">{{ t('batchTools.deleteConfirmTitle') }}</h2>
                        <button class="close-btn" @click="deleteDialogVisible = false">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2"
                                    stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </button>
                    </div>

                    <!-- 提示文案 -->
                    <p class="modal-subtitle">
                        {{ t('batchTools.deleteConfirmMessage') }}
                    </p>

                    <!-- 底部按钮 -->
                    <div class="modal-footer">
                        <button class="btn btn-cancel" :disabled="deleteChatLoading"
                            @click="deleteDialogVisible = false">
                            {{ t('batchTools.cancel') }}
                        </button>
                        <button class="btn btn-confirm" :disabled="deleteChatLoading" @click="handleConfirmDelete">
                            <span v-if="deleteChatLoading" class="btn-loading-spinner" />
                            {{ t('batchTools.confirm') }}
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>

    <!-- 删除聊天确认弹窗 -->
    <Teleport to="body">
        <Transition name="modal-fade">
            <div v-if="deleteChatDialogVisible" class="delete-chat-modal-overlay">
                <div class="delete-chat-modal">
                    <!-- 标题行 -->
                    <div class="modal-header">
                        <h2 class="modal-title">{{ t('chat.deleteChatTitle') }}</h2>
                        <button class="close-btn" @click="handleCancelDeleteChat">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2"
                                    stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </button>
                    </div>

                    <!-- 提示文案 -->
                    <p class="modal-subtitle">
                        {{ t('chat.deleteChatConfirmMessage', { name: chatToDelete?.title || '' }) }}
                    </p>

                    <!-- 底部按钮 -->
                    <div class="modal-footer">
                        <button class="btn btn-cancel" :disabled="deleteChatLoading" @click="handleCancelDeleteChat">
                            {{ t('chat.cancel') }}
                        </button>
                        <button class="btn btn-confirm" :disabled="deleteChatLoading" @click="handleConfirmDeleteChat">
                            <span v-if="deleteChatLoading" class="btn-loading-spinner" />
                            {{ t('chat.delete') }}
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from '@/composables/useI18n'
import { useAppStore } from '@/stores/app'
import { useChatDialogueStore } from '~/stores/chatDialogue'
import { useLocalePath, navigateTo } from '#imports'
import { showNotification } from '@/utils/common'
import {
    getConversationsApi,
    deleteConversationApi,
    updateConversationNameApi,
    type ConversationItem
} from '~/api/chat'
import { analyticsEvent } from '~/utils/log-event-func'

const localePath = useLocalePath()
const chatDialogueStore = useChatDialogueStore()
const appStore = useAppStore()

/**
 * 侧边栏类型
 * - chat: 聊天历史记录
 * - project: 我的项目
 * - empty: 空侧栏
 */
export type SidebarType = 'chat' | 'project' | 'empty'

/**
 * 聊天历史记录项
 */
interface ChatHistoryItem {
    id: string | number
    title: string
    date: string
    create_time: number
}

/**
 * 项目项
 */
interface ProjectItem {
    id: string | number
    name: string
    createTime?: string
}

const props = withDefaults(
    defineProps<{
        /** 控制显示/隐藏 */
        visible?: boolean
        /** 类型：chat（聊天历史）| project（我的项目） */
        type?: SidebarType
        /** 阻止内部路由跳转（工具页嵌入时使用），仅 emit 事件让父组件处理 */
        preventNavigation?: boolean
        /** PC 端侧边栏位置 */
        position?: 'left' | 'right'
        /** PC 端隐藏默认标题栏 */
        hideHeader?: boolean
        /** 工具页等场景：PC 宽度填满父级（默认固定 216px 便于独立页收起动画） */
        stretchToParent?: boolean
    }>(),
    {
        visible: true,
        type: 'chat',
        preventNavigation: false,
        position: 'right',
        hideHeader: false,
        stretchToParent: false,
    }
)

const emit = defineEmits<{
    /** 切换侧边栏显示/隐藏（PC端） */
    (e: 'toggle'): void
    /** 关闭抽屉（移动端） */
    (e: 'close'): void
    /** 选中某条记录/项目，返回详情和类型 */
    (e: 'select', item: ChatHistoryItem | ProjectItem, type: SidebarType): void
    /** 新建聊天完成（preventNavigation 模式下使用） */
    (e: 'new-chat'): void
    /** 删除了当前会话（preventNavigation 模式下使用） */
    (e: 'conversation-deleted'): void
}>()

// ==================== 设备判断 ====================

/** 移动端断点 */
const MOBILE_BREAKPOINT = 1024

/** 是否为移动端 */
const isMobile = ref(false)

/**
 * 更新设备类型
 */
const updateDeviceType = () => {
    if (typeof window !== 'undefined') {
        isMobile.value = window.innerWidth < MOBILE_BREAKPOINT
    }
}

onMounted(() => {
    updateDeviceType()
    window.addEventListener('resize', updateDeviceType)

    // 首次数据加载必须晚于 hydration，避免客户端加载态与 SSR 空状态结构不一致。
    loadDataForType(props.type)
})

onUnmounted(() => {
    window.removeEventListener('resize', updateDeviceType)
})

/**
 * 关闭抽屉（移动端）
 */
const handleClose = () => {
    activeMenuId.value = null
    emit('close')
}

const { t } = useI18n()

// ==================== 聊天历史记录相关 ====================

/** 聊天历史记录列表 */
const chatHistoryList = ref<ChatHistoryItem[]>([])

/** 分页状态 */
const chatLoading = ref(false)
const chatFinished = ref(false)
const chatOffset = ref(0)
const pageSize = 20
/** 列表加载状态 */
const isInitialLoading = ref(false)

/** 删除聊天确认弹窗状态 */
const deleteChatDialogVisible = ref(false)
const chatToDelete = ref<ChatHistoryItem | null>(null)
const deleteChatLoading = ref(false)

/** 重命名聊天弹窗状态（PC） */
const renameChatDialogVisible = ref(false)
const chatToRename = ref<ChatHistoryItem | null>(null)
const renameChatInput = ref('')
const renameChatLoading = ref(false)
const RENAME_CHAT_MAXLEN = 30

const renameChatIsExceed = computed(() => {
    return (renameChatInput.value || '').length > RENAME_CHAT_MAXLEN
})

const canConfirmRenameChat = computed(() => {
    if (renameChatLoading.value) return false
    if (renameChatIsExceed.value) return false
    const nextName = renameChatInput.value.trim()
    if (!nextName) return false
    const currentName = (chatToRename.value?.title || '').trim()
    if (nextName === currentName) return false
    return true
})

/**
 * 格式化时间戳为日期字符串（聊天历史用）
 */
const formatChatDate = (timestamp: number): string => {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/**
 * 加载聊天历史记录
 * 服务端按时间倒序返回（新→旧），最新的在第一页
 * 往下滚动加载更早的历史，追加到列表末尾
 * @param isLoadMore 是否是加载更多（滚动加载）
 * @param forceRefresh 是否强制刷新（忽略 loading 状态，用于新建对话后刷新）
 */
const loadChatHistory = async (isLoadMore = false, forceRefresh = false) => {
    // 强制刷新时跳过 loading 检查，但仍需检查加载更多的完成状态
    if (!forceRefresh && chatLoading.value) return
    if (isLoadMore && chatFinished.value) return
    if (!import.meta.client) return

    chatLoading.value = true
    if (!isLoadMore) {
        isInitialLoading.value = true
    }

    try {
        const currentTeam = JSON.parse(localStorage?.getItem?.('currentTeam') || '{}')

        const res = await getConversationsApi({
            teamId: currentTeam?.id || '',
            offset: isLoadMore ? chatOffset.value : 0,
            pageSize
        })

        if (res.status === 200) {
            const items = (res.data?.conversions || []).map((item: ConversationItem) => ({
                id: item.id,
                title: item.name,
                date: formatChatDate(item.create_time),
                create_time: item.create_time
            }))

            if (isLoadMore) {
                // 加载更多（更早的历史），追加到列表末尾
                chatHistoryList.value.push(...items)
            } else {
                chatHistoryList.value = items
            }

            chatOffset.value = res.data?.next_offset || 0
            chatFinished.value = !res.data?.next_offset || items.length < pageSize
        }
    } catch (error) {
        console.error('[ChatSidebar] loadChatHistory error:', error)
    } finally {
        chatLoading.value = false
        isInitialLoading.value = false
    }
}

/**
 * 滚动事件处理（往下滚动加载更早的历史）
 */
const handleScroll = (e: Event) => {
    if (props.type !== 'chat') return
    const target = e.target as HTMLElement
    const { scrollTop, scrollHeight, clientHeight } = target
    // 滚动到底部附近时，加载更早的历史
    if (scrollHeight - scrollTop - clientHeight < 100) {
        loadChatHistory(true)
    }
}

/**
 * 新建聊天
 */
const handleNewChat = async () => {
    analyticsEvent('click_send_or_create', {
        message_type: 'text_message',
        generate_from: 'sidebar_click_generate',
    })

    try {
        const success = await chatDialogueStore.createConversation(true)

        if (success && chatDialogueStore.conversationId) {
            chatOffset.value = 0
            chatFinished.value = false
            await loadChatHistory()

            if (props.preventNavigation) {
                emit('new-chat')
            } else {
                navigateTo(localePath(`/chat/${chatDialogueStore.conversationId}`))
            }
        }
    } catch (error) {
        console.error('[ChatSidebar] handleNewChat error:', error)
    }
}

/**
 * 打开删除聊天确认弹窗
 */
const openDeleteChatDialog = (item: ChatHistoryItem) => {
    chatToDelete.value = item
    deleteChatDialogVisible.value = true
    activeMenuId.value = null
    if (isMobile.value) handleClose()
}

/**
 * 打开重命名聊天弹窗（PC）
 */
const openRenameChatDialog = (item: ChatHistoryItem) => {
    chatToRename.value = item
    renameChatInput.value = item.title || ''
    renameChatDialogVisible.value = true
    activeMenuId.value = null
    if (isMobile.value) handleClose()
}

/**
 * 取消重命名聊天
 */
const handleCancelRenameChat = () => {
    if (renameChatLoading.value) return
    renameChatDialogVisible.value = false
    chatToRename.value = null
    renameChatInput.value = ''
}

/**
 * 确认重命名聊天
 */
const handleConfirmRenameChat = async () => {
    if (!chatToRename.value || !canConfirmRenameChat.value) return
    if (renameChatLoading.value) return

    const id = String(chatToRename.value.id)
    const nextName = renameChatInput.value.trim()
    renameChatLoading.value = true

    try {
        const res = await updateConversationNameApi(id, {
            update_type: 'NAME',
            name: nextName,
        })

        if (res.status === 200 || res.success) {
            const target = chatHistoryList.value.find(it => String(it.id) === id)
            if (target) target.title = nextName

            showNotification.success(t('chat.renameSuccess'))
            renameChatDialogVisible.value = false
            chatToRename.value = null
            renameChatInput.value = ''
        } else {
            showNotification.error(res.message || t('chat.renameFailed'))
        }
    } catch (error) {
        console.error('[ChatSidebar] handleConfirmRenameChat error:', error)
        showNotification.error(t('chat.renameFailed'))
    } finally {
        renameChatLoading.value = false
    }
}

/**
 * 取消删除聊天
 */
const handleCancelDeleteChat = () => {
    deleteChatDialogVisible.value = false
    chatToDelete.value = null
}

/**
 * 确认删除聊天
 */
const handleConfirmDeleteChat = async () => {
    if (!chatToDelete.value || deleteChatLoading.value) return

    const id = chatToDelete.value.id
    deleteChatLoading.value = true
    try {
        const res = await deleteConversationApi(String(id))
        if (res.status === 200 || res.success) {
            // 先关闭弹窗，再更新列表
            deleteChatDialogVisible.value = false
            chatToDelete.value = null

            chatHistoryList.value = chatHistoryList.value.filter(item => item.id !== id)

            showNotification.success(t('chat.deleteSuccess'))

            if (chatDialogueStore.conversationId === String(id)) {
                chatDialogueStore.initNewConversation()
                if (props.preventNavigation) {
                    emit('conversation-deleted')
                } else {
                    navigateTo(localePath('/chat'))
                }
            }
        } else {
            showNotification.error(res.message || t('chat.deleteFailed'))
        }
    } catch (error) {
        console.error('[ChatSidebar] handleConfirmDeleteChat error:', error)
        showNotification.error(t('chat.deleteFailed'))
    } finally {
        deleteChatLoading.value = false
        deleteChatDialogVisible.value = false
        chatToDelete.value = null
    }
}

// ==================== 我的项目相关 ====================

/**
 * 项目列表（从 appStore 获取，过滤并去重）
 */
const projectList = computed(() => {
    if (!appStore.projectList || !Array.isArray(appStore.projectList)) {
        return []
    }

    let filtered = appStore.projectList.filter(
        (item: any) => item != null && item.id != null
    )

    const seenIds = new Set()
    filtered = filtered.filter((item: any) => {
        if (seenIds.has(item.id)) return false
        seenIds.add(item.id)
        return true
    })

    if (filtered.length > 1) {
        filtered = filtered.filter((item: any) => item.id !== -1)
    }

    return filtered
})

// 项目弹窗状态
const projectDialogVisible = ref(false)
const projectNameInput = ref('')
const editingProject = ref<ProjectItem | null>(null)
const projectLoading = ref(false)

// 删除确认弹窗状态
const deleteDialogVisible = ref(false)
const deleteLoading = ref(false)
const projectToDelete = ref<ProjectItem | null>(null)

/**
 * 格式化日期（项目用）
 */
const formatProjectDate = (dateStr?: string) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/**
 * 打开新增项目弹窗
 */
const handleAddProject = () => {
    editingProject.value = null
    projectNameInput.value = ''
    projectDialogVisible.value = true
}

/**
 * 打开编辑项目弹窗
 */
const handleEditProject = (item: ProjectItem) => {
    activeMenuId.value = null
    editingProject.value = item
    projectNameInput.value = item.name || ''
    projectDialogVisible.value = true
}

/**
 * 提交项目（新增或编辑）
 */
const handleSubmitProject = async () => {
    if (!projectNameInput.value.trim()) {
        return
    }

    projectLoading.value = true
    try {
        const isEdit = editingProject.value?.id && editingProject.value.id !== -1
        if (isEdit) {
            await appStore.updateProject(String(editingProject.value!.id), {
                name: projectNameInput.value.trim()
            })
        } else {
            await appStore.createProject({
                name: projectNameInput.value.trim()
            })
        }
        projectDialogVisible.value = false
        projectNameInput.value = ''
        editingProject.value = null

        // 移动端新增项目后，收起项目列表抽屉
        if (isMobile.value && !isEdit) {
            handleClose()
        }
    } finally {
        projectLoading.value = false
    }
}

/**
 * 打开删除确认弹窗
 */
const handleDeleteProject = (item: ProjectItem) => {
    activeMenuId.value = null
    projectToDelete.value = item
    deleteDialogVisible.value = true
}

/**
 * 确认删除项目
 */
const handleConfirmDelete = async () => {
    if (!projectToDelete.value?.id) {
        deleteDialogVisible.value = false
        return
    }

    deleteLoading.value = true
    try {
        await appStore.deleteProject(String(projectToDelete.value.id))
        deleteDialogVisible.value = false
        projectToDelete.value = null
    } finally {
        deleteLoading.value = false
    }
}

/**
 * 选中项目
 */
const handleSelectProject = (item: ProjectItem) => {
    appStore.changeCurrentProject(item)
    emit('select', item, 'project')
}

// ==================== 公共逻辑 ====================

/** 当前展开菜单的 item id */
const activeMenuId = ref<number | string | null>(null)

/** 切换操作菜单 */
const toggleItemMenu = (id: number | string) => {
    activeMenuId.value = activeMenuId.value === id ? null : id
}

// ===== PC hover menu behavior (chat history list) =====
let closeMenuTimer: ReturnType<typeof setTimeout> | null = null

const cancelCloseItemMenu = () => {
    if (closeMenuTimer) {
        clearTimeout(closeMenuTimer)
        closeMenuTimer = null
    }
}

const openItemMenu = (id: number | string) => {
    cancelCloseItemMenu()
    activeMenuId.value = id
}

const scheduleCloseItemMenu = (id: number | string) => {
    cancelCloseItemMenu()
    closeMenuTimer = setTimeout(() => {
        if (activeMenuId.value === id) {
            activeMenuId.value = null
        }
        closeMenuTimer = null
    }, 150)
}

/**
 * 选中某条历史记录（聊天）- PC端
 */
const handleSelectItem = (item: ChatHistoryItem) => {
    emit('select', item, 'chat')
    if (!props.preventNavigation) {
        navigateTo(localePath(`/chat/${item.id}`))
    }
}

/**
 * 选中某条历史记录（聊天）- 移动端（选中后关闭抽屉）
 */
const handleSelectItemMobile = (item: ChatHistoryItem) => {
    emit('select', item, 'chat')
    if (!props.preventNavigation) {
        navigateTo(localePath(`/chat/${item.id}`))
    }
    handleClose()
}

/**
 * 选中项目 - 移动端（选中后关闭抽屉）
 */
const handleSelectProjectMobile = (item: ProjectItem) => {
    appStore.changeCurrentProject(item)
    emit('select', item, 'project')
    handleClose()
}

/** visible 变化时加载数据（移动端抽屉打开时） */
watch(() => props.visible, async (newVal) => {
    if (newVal) {
        if (props.type === 'chat') {
            chatOffset.value = 0
            chatFinished.value = false
            loadChatHistory()
        } else if (props.type === 'project') {
            if (!appStore.projectList?.length) {
                await appStore.getProjectList()
            }
        }
    }
    if (!newVal) {
        cancelCloseItemMenu()
        activeMenuId.value = null
    }
})

/** 按侧边栏类型加载对应数据（chat：历史会话；project：创作项目） */
const loadDataForType = (type: string) => {
    if (type === 'chat') {
        chatOffset.value = 0
        chatFinished.value = false
        loadChatHistory()
    } else if (type === 'project' && !appStore.projectList?.length) {
        return appStore.getProjectList()
    }
}

/** 类型变化时重新加载数据 */
watch(() => props.type, (newType) => {
    cancelCloseItemMenu()
    activeMenuId.value = null
    loadDataForType(newType)
})

// 获取当前对话标题
const currentChatTitle = computed(() => {
    if (!chatDialogueStore.conversationId) return ''
    const currentItem = chatHistoryList.value.find(
        item => String(item.id) === chatDialogueStore.conversationId
    )
    return currentItem?.title || ''
})

// 暴露方法给父组件调用
defineExpose({
    loadChatHistory,
    currentChatTitle
})
</script>

<style lang="less" scoped>
.mobile-customize-style {
    .rename-chat-close {
        display: none;
    }

    .rename-chat-header {
        justify-content: center;
    }

    .rename-chat-modal .el-input__wrapper {
        background: rgba(0, 0, 0, 0.48);
    }
}

.chat-sidebar {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 216px;
    height: 100%;
    background-color: rgba(16, 16, 18, 1);
    flex-shrink: 0;
}

/* 嵌入工具页右侧壳层时由外层 flex 百分比控制宽度，不再锁 216px */
.chat-sidebar.chat-sidebar--stretch {
    width: 100%;
    max-width: none;
}

@media (min-width: 1024px) {
    /*
     * 展开/收起必须能在两个「可插值」状态之间过渡：
     * - 不要用 max-width: 0 !important 再变回默认 none（无法插值，展开会瞬间完成）
     * - 不要用 width !important（部分环境下从 !important 回到普通值不触发动画）
     * 显式固定展开态 width / min / max 均为 216px，收起态均为 0，两侧一致可过渡。
     */
    .chat-sidebar {
        width: 216px;
        min-width: 0;
        max-width: 216px;
        opacity: 1;
        transition:
            width 0.42s cubic-bezier(0.33, 0.86, 0.2, 1),
            min-width 0.42s cubic-bezier(0.33, 0.86, 0.2, 1),
            max-width 0.42s cubic-bezier(0.33, 0.86, 0.2, 1),
            opacity 0.35s ease,
            border-color 0.35s ease,
            border-width 0.35s ease,
            padding 0.35s ease;
    }

    .chat-sidebar.chat-sidebar--stretch {
        width: 100%;
        max-width: none;
    }

    .chat-sidebar.chat-sidebar--pc-hidden {
        width: 0;
        min-width: 0;
        max-width: 0;
        opacity: 0;
        overflow: hidden;
        pointer-events: none;
        padding-left: 0;
        padding-right: 0;
    }

    .chat-sidebar.chat-sidebar--pc-hidden.chat-sidebar--right {
        border-left-width: 0;
    }

    .chat-sidebar.chat-sidebar--pc-hidden.chat-sidebar--left {
        border-right-width: 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    @media (min-width: 1024px) {
        .chat-sidebar {
            transition: none;
        }
    }
}

.chat-sidebar--left {
    order: -1;
    border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.chat-sidebar--right {
    border-left: 1px solid rgba(255, 255, 255, 0.08);
}

.chat-sidebar--headerless {
    .sidebar-content {
        padding-top: 8px;
    }

    .sidebar-footer {
        padding-top: 12px;
    }
}

.sidebar-section-title {
    margin-top: 12px;
    padding: 4px 12px 0 28px;
    margin-bottom: 10px;
    color: rgba(255, 255, 255, 0.60);
    font-family: "PingFang SC";
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: 15px;
    letter-spacing: 1px;
    text-transform: uppercase;
}

/* 顶部标题栏 */
.sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 12px;
    border-bottom: 1px solid #1f1f21;
}

.toggle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    color: #fff;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
    }
}

.toggle-icon {
    width: 20px;
    height: 20px;
    transform: rotate(180deg);
}

.toggle-icon--left {
    transform: none;
}

.sidebar-title {
    font-size: 14px;
    font-weight: 500;
    color: #fff;
}

/* 内容区域 */
.sidebar-content {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    position: relative;

    /* 自定义滚动条 */
    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: rgba(255, 255, 255, 0.15);
        border-radius: 2px;

        &:hover {
            background-color: rgba(255, 255, 255, 0.25);
        }
    }
}

/* 加载状态 */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    gap: 16px;
}

.loading-text {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
}

/* 空状态 */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    position: absolute;
    top: 45%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    padding: 0 20px;
}

.empty-image {
    width: 150px;
    height: auto;
    margin-bottom: 16px;
    opacity: 0.6;
}

.empty-title {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
}

/* 历史记录列表 */
.history-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.history-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background-color: #1C1C20;

        .item-title {
            color: rgba(255, 255, 255, 0.90) !important;
        }
    }

    &.is-active {
        background: #1C1C20;

        .item-title {
            color: rgba(255, 255, 255, 0.90) !important;
        }

    }
}

.item-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
}

.item-title {
    font-size: 14px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.60);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.item-date {
    font-size: 12px;
    font-weight: 300;
    color: rgba(255, 255, 255, 0.4);
}

.item-actions {
    position: relative;
    flex-shrink: 0;
}

/* 三个点按钮 */
.more-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    color: rgba(255, 255, 255, 0.4);
    background: transparent;
    border: none;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
        color: rgba(255, 255, 255, 0.8);
    }
}

/* 仅 PC 侧边栏：hover 行才显示三点按钮（不改变布局，避免 UI 抖动） */
.chat-sidebar .history-item .more-btn {
    opacity: 0;
    pointer-events: none;
}

.chat-sidebar .history-item:hover .more-btn {
    opacity: 1;
    pointer-events: auto;
}

.more-icon {
    width: 18px;
    height: 18px;
}

/* 删除弹出框 */
.delete-popup {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 4px;
    z-index: 10;
}

.delete-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 100px;
    height: 36px;
    padding: 0 12px;
    font-size: 13px;
    color: rgba(212, 75, 75, 1);
    background-color: rgba(28, 28, 30, 1);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        border-color: rgba(212, 75, 75, 1);
    }
}

.delete-icon {
    width: 16px;
    height: 16px;
    object-fit: contain;
}

/* 弹出框动画 */
.popup-enter-active,
.popup-leave-active {
    transition: all 0.2s ease;
}

.popup-enter-from,
.popup-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}

/* 操作弹出框（编辑 + 删除） */
.action-popup {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 4px;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
    background-color: rgba(28, 28, 30, 1);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    min-width: 130px;
}

.edit-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: 32px;
    padding: 0 10px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.9);
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
}

.edit-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
}

.action-popup .delete-btn {
    width: 100%;
    min-width: auto;
    height: 32px;
    padding: 0 10px;
    border: none;
    border-radius: 6px;
}

/* 底部按钮 */
.sidebar-footer {
    padding: 20px;
}

.new-chat-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    height: 44px;
    font-size: 12px;
    font-weight: 500;
    color: #000;
    background-color: rgba(153, 247, 104, 1);
    border: none;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;


    &:hover {
        opacity: 0.9;
    }

    &:active {
        transform: scale(0.98);
    }
}

.plus-icon {
    width: 16px;
    height: 16px;
}

.chat-add-icon {
    width: 16px;
    height: 16px;
    object-fit: contain;
}

/* 空状态下的新建按钮样式（覆盖宽度） */
.empty-new-chat-btn {
    width: 100%;
    margin-top: 20px;
    border: 1px solid rgba(255, 255, 255, 0.10);
    border-radius: 12px;
    background: #1c1c1e;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.10), 0 4px 6px -4px rgba(0, 0, 0, 0.10);
    color: #D1D5DC;
    transition: all 0.3s ease;

    &:hover {
        color: var(--weryai-text-active);
        transform: scale(1.05);
    }

}

/* fade 动画 */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* ==================== 移动端抽屉模式样式 ==================== */

.history-drawer-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    display: flex;
    justify-content: flex-end;
}

.drawer-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
}

.drawer-container {
    position: relative;
    width: 100%;
    max-width: 336px;
    height: 100%;
    background-color: #1a1a1c;
    border-left: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0px 25px 50px -12px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
}

.drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 21px 15px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.drawer-title {
    font-size: 16px;
    font-weight: 500;
    line-height: 24.5px;
    color: #fff;
}

.close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.05);
    border: none;
    border-radius: 9999px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        color: #fff;
        background: rgba(255, 255, 255, 0.1);
    }
}

.close-icon {
    width: 16px;
    height: 16px;
}

.drawer-content {
    flex: 1;
    overflow-y: auto;
    padding: 14px;
}

.drawer-footer {
    background: rgba(0, 0, 0, 0.1);
    border-top: 1px solid rgba(217, 217, 217, 0.1);
    padding: 15px 14px 14px;
}

/* 抽屉动画 */
.drawer-enter-active,
.drawer-leave-active {
    transition: all 0.3s ease;
}

.drawer-enter-active .drawer-overlay,
.drawer-leave-active .drawer-overlay {
    transition: opacity 0.3s ease;
}

.drawer-enter-active .drawer-container,
.drawer-leave-active .drawer-container {
    transition: transform 0.3s ease;
}

.drawer-enter-from,
.drawer-leave-to {
    .drawer-overlay {
        opacity: 0;
    }

    .drawer-container {
        transform: translateX(100%);
    }
}

/* 移动端列表项样式覆盖 */
.history-drawer-wrapper {
    .history-list {
        gap: 7px;
    }

    .history-item {
        width: 100%;
        height: auto;
        padding: 15px;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.05);
        border-radius: 12px;

        &.is-active {
            border-color: #99f768;
            background: #1C1C20;

            .item-title {
                color: #fff;
            }

            .chat-icon {
                opacity: 1;
            }
        }

        &:not(.is-active) {
            .item-title {
                color: rgba(217, 217, 217, 0.5);
            }

            .chat-icon {
                opacity: 0.5;
            }
        }
    }

    .item-left {
        display: flex;
        align-items: center;
        gap: 10.5px;
        flex: 1;
        min-width: 0;
    }

    .chat-icon {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        object-fit: contain;
        transition: opacity 0.2s ease;
    }

    .item-info {
        gap: 6px;
    }

    .item-title {
        font-size: 14px;
        font-weight: 600;
    }

    .item-date {
        display: none;
    }

    .new-chat-btn {
        width: 100%;
        height: 48px;
        border-radius: 10.5px;
        font-size: 14px;
        font-weight: 600;
        color: #0f0f0f;
    }

    .chat-add-icon {
        width: 20px;
        height: 20px;
    }

    .empty-image {
        width: 200px;
    }

    .empty-state .new-chat-btn {
        width: 100%;
        margin-top: 24px;
    }
}

/* 移动端垃圾桶按钮 */
.delete-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 7px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.5);
    transition: color 0.2s ease;

    &:hover {
        color: rgba(255, 255, 255, 0.8);
    }
}

.trash-icon {
    width: 16px;
    height: 16px;
    object-fit: contain;
    filter: brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%);
    opacity: 0.5;
    transition: opacity 0.2s ease;
}

/* ==================== 删除聊天确认弹窗样式 ==================== */

.delete-chat-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.delete-chat-modal {
    position: relative;
    width: 449px;
    max-width: calc(100vw - 40px);
    background: #1c1c20;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    padding: 30px;
    box-shadow: 0px 20px 60px rgba(0, 0, 0, 0.5);

    /* 移动端适配 */
    @media (max-width: 1023px) {
        width: calc(100vw - 32px);
        max-width: 400px;
        padding: 24px 20px;
        border-radius: 20px;
    }

    .modal-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: 20px;
    }

    .modal-title {
        font-size: 20px;
        font-weight: 500;
        color: #ffffff;
        margin: 0;
    }

    .close-btn {
        width: 24px;
        height: 24px;
        background: transparent;
        border: none;
        color: rgba(255, 255, 255, 0.6);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        transition: color 0.2s;

        &:hover {
            color: rgba(255, 255, 255, 1);
        }
    }

    .modal-subtitle {
        font-size: 14px;
        color: #777779;
        margin: 0 0 20px 0;
        line-height: 20px;
    }

    .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
    }

    .btn {
        min-width: 90px;
        height: 40px;
        border-radius: 8px;
        border: none;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: opacity 0.2s;
        padding: 10px;
    }

    .btn-cancel {
        background: rgba(255, 255, 255, 0.2);
        color: #ffffff;

        &:hover {
            background: rgba(255, 255, 255, 0.25);
        }
    }

    .btn-confirm {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        background: #99f768;
        color: #000000;

        &:hover {
            background: rgba(153, 247, 104, 0.9);
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    .btn-cancel:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .btn-loading-spinner {
        width: 14px;
        height: 14px;
        border: 2px solid rgba(0, 0, 0, 0.2);
        border-top-color: #000;
        border-radius: 50%;
        animation: btn-spin 0.8s linear infinite;
    }

    @keyframes btn-spin {
        to {
            transform: rotate(360deg);
        }
    }

    /* 移动端适配 */
    @media (max-width: 1023px) {
        .modal-header {
            margin-bottom: 16px;
        }

        .modal-title {
            font-size: 18px;
        }

        .close-btn {
            width: 20px;
            height: 20px;

            svg {
                width: 20px;
                height: 20px;
            }
        }

        .modal-subtitle {
            font-size: 13px;
            margin-bottom: 20px;
        }

        .modal-footer {
            gap: 10px;
        }

        .btn {
            min-width: 80px;
            height: 38px;
            font-size: 14px;
            border-radius: 8px;
        }
    }
}

/* 弹窗动画 */
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}

.modal-fade-enter-active .delete-chat-modal,
.modal-fade-leave-active .delete-chat-modal {
    transition: transform 0.2s ease;
}

.modal-fade-enter-from .delete-chat-modal,
.modal-fade-leave-to .delete-chat-modal {
    transform: scale(0.95);
}

/* ==================== 重命名聊天弹窗（PC）UI ==================== */
.rename-chat-modal {
    width: 606px;
    max-width: 92vw;
    height: 243px;
    border-radius: 24px;
    border: 1px solid rgba(255, 255, 255, 0.10);
    background: #1C1C20;
    box-shadow: 0 20px 60px 0 rgba(0, 0, 0, 0.50);
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.rename-chat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.rename-chat-title {
    color: #FFF;
    font-family: Roboto;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
}

.rename-chat-close {
    width: 28px;
    height: 28px;
    line-height: 28px;
    border-radius: 10px;
    color: #fff;
    font-size: 28px;
    background: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.rename-chat-body {
    flex: 1;
    display: flex;
    align-items: center;
}

.rename-chat-input-wrap {
    width: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
}

.rename-chat-count {
    position: absolute;
    right: 12px;
    bottom: 7px;
    font-size: 12px;
    line-height: 1;
    color: rgba(255, 255, 255, 0.35);
    pointer-events: none;
    color: #5C5E69;
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}

.rename-chat-count.exceed {
    color: #CE6767;
    bottom: 30px;
}

/* Reserve space for the count text (replaces ElementPlus show-word-limit UI) */
::deep(.rename-chat-modal .rename-chat-input .el-textarea__inner) {
    min-height: 44px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.04);
    box-shadow: none !important;
    border: 1px solid rgba(255, 255, 255, 0.10);
    padding: 10px 56px 22px 12px;
    color: #fff !important;
    line-height: 22px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}

::deep(.rename-chat-modal .rename-chat-input .el-textarea__inner:focus) {
    box-shadow: none !important;
    border-color: rgba(255, 255, 255, 0.16);
}

/* textarea exceed state (rename modal) */
::deep(.rename-chat-modal .rename-chat-input.is-exceed .el-textarea__inner) {
    border-color: #CE6767 !important;
    background: rgba(226, 123, 123, 0.08) !important;
}

.rename-chat-limit-tip {
    margin-top: 8px;
    font-size: 12px;
    line-height: 16px;
    color: #CE6767;
}

:deep(.rename-chat-modal .el-input.is-exceed .el-input__wrapper) {
    border: 1px solid #CE6767 !important;
    background: rgba(226, 123, 123, 0.08) !important;
}

:deep(.rename-chat-modal .el-input__count) {
    color: rgba(255, 255, 255, 0.35) !important;
}

:deep(.rename-chat-modal .el-input.is-exceed .el-input__count) {
    color: #CE6767 !important;
}

.rename-chat-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

.rename-chat-btn {
    color: #FFF;
    font-family: "Source Han Sans CN";
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    padding: 10px;
    min-width: 90px;
    border-radius: 8px;
}

/* ElementPlus input: fixed height + remove focus ring (scoped to modal) */
.rename-chat-input {
    width: 100%;
}

:deep(.rename-chat-modal .el-input) {
    width: 100%;
}

:deep(.rename-chat-modal .el-input__wrapper) {
    height: 44px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.04);
    box-shadow: none !important;
    border: 1px solid rgba(255, 255, 255, 0.10);
}

:deep(.rename-chat-modal .el-input__wrapper.is-focus) {
    box-shadow: none !important;
    border-color: rgba(255, 255, 255, 0.16);
}

/* Buttons: keep style local, no global impact */
:deep(.rename-chat-modal .rename-chat-btn-cancel) {
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.10);
    color: rgba(255, 255, 255, 0.9);
}

:deep(.rename-chat-modal .rename-chat-btn-cancel:hover) {
    background: rgba(255, 255, 255, 0.16);
}

:deep(.rename-chat-modal .rename-chat-btn-confirm) {
    background: #99F768;
    border: 1px solid transparent;
    color: #0f0f0f;
}

:deep(.rename-chat-modal .rename-chat-btn-confirm:hover) {
    background: rgba(153, 247, 104, 0.9);
}
</style>
